import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

export type PermissionSensitivity = 'STANDARD' | 'SENSITIVE'
export type PermissionGrantedBy = 'ACCOUNT_ADMIN_CLIENT' | 'UNIXO_INTRANET'
export type PermissionStatus = 'ACTIVE' | 'DEPRECATED' | 'UNUSED'

export interface PortalPermissionItem {
  uuid: string
  code: string
  description: string | null

  label: string | null
  shortDescription: string | null
  longDescription: string | null
  whenToGrant: string | null
  whenNotToGrant: string | null

  category: string | null
  sensitivity: PermissionSensitivity
  grantedBy: PermissionGrantedBy
  status: PermissionStatus

  contactsCount: number
  usageCount: number
}

interface PortalPermissionContact {
  contactUuid: string
  fullName: string
  email: string | null
  companyUuid: string
  companyName: string | null
  grantedAt: string
}

export interface PortalPermissionDetail extends PortalPermissionItem {
  contacts: PortalPermissionContact[]
  apiUsages?: unknown
  frontUsages?: unknown
  routeUsages?: unknown
}

export interface PortalPermissionCreatePayload {
  code: string
  description?: string | null
  label?: string | null
  shortDescription?: string | null
  longDescription?: string | null
  whenToGrant?: string | null
  whenNotToGrant?: string | null
  category?: string | null
  sensitivity?: PermissionSensitivity
  grantedBy?: PermissionGrantedBy
  status?: PermissionStatus
}

export type PortalPermissionUpdatePayload = Omit<Partial<PortalPermissionCreatePayload>, 'code'>

type StoreResult<T> = { ok: true; data: T } | { ok: false; message: string }

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json()
    if (body?.message) return Array.isArray(body.message) ? body.message.join(', ') : String(body.message)
  } catch {
    // ignore
  }
  return fallback
}

export const usePortalPermissionAdminStore = defineStore('portalPermissionAdmin', () => {
  const suiteStore = useSuiteStore()
  const items = ref<PortalPermissionItem[]>([])
  const availableCodes = ref<string[]>([])
  const isLoading = ref(false)

  async function fetchAvailableCodes(): Promise<string[]> {
    const res = await suiteStore.suiteFetch('portal-permissions/admin/available-codes', {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      availableCodes.value = (await res.json()) as string[]
      return availableCodes.value
    }
    return []
  }

  async function fetchAll(): Promise<PortalPermissionItem[]> {
    isLoading.value = true
    try {
      const res = await suiteStore.suiteFetch('portal-permissions/admin', {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        items.value = (await res.json()) as PortalPermissionItem[]
        return items.value
      }
    } catch (e) {
      console.error('Erreur récupération portal permissions', e)
    } finally {
      isLoading.value = false
    }
    return []
  }

  async function fetchDetail(uuid: string): Promise<PortalPermissionDetail | null> {
    const res = await suiteStore.suiteFetch(`portal-permissions/admin/${uuid}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return (await res.json()) as PortalPermissionDetail
    }
    return null
  }

  async function create(payload: PortalPermissionCreatePayload): Promise<StoreResult<PortalPermissionItem>> {
    const res = await suiteStore.suiteFetch('portal-permissions/admin', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as PortalPermissionItem
      items.value.push(data)
      items.value.sort((a, b) => a.code.localeCompare(b.code))
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la création')
      : 'Erreur lors de la création'
    return { ok: false, message }
  }

  async function update(uuid: string, payload: PortalPermissionUpdatePayload): Promise<StoreResult<PortalPermissionItem>> {
    const res = await suiteStore.suiteFetch(`portal-permissions/admin/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as PortalPermissionItem
      const idx = items.value.findIndex((i) => i.uuid === uuid)
      if (idx >= 0) items.value[idx] = data
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la mise à jour')
      : 'Erreur lors de la mise à jour'
    return { ok: false, message }
  }

  return {
    items,
    availableCodes,
    isLoading,
    fetchAvailableCodes,
    fetchAll,
    fetchDetail,
    create,
    update,
  }
})
